import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { CommunityHeader } from "@/components/community/CommunityHeader";

import { DatasetCard } from "@/components/community/datasets/DatasetCard";
import { AddDatasetDialog } from "@/components/community/datasets/AddDatasetDialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useIsAdmin } from "@/hooks/useUserRole";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useToast } from "@/components/ui/use-toast";

const CATEGORIES = ["Todos", "SQL", "Python", "Excel", "Power BI", "Machine Learning", "Geral"];

interface Dataset {
  id: string;
  title: string;
  description: string;
  external_url: string;
  category: string;
  difficulty: string;
  source: string | null;
  is_active: boolean;
}

const Datasets = () => {
  const { isAdmin } = useIsAdmin();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDataset, setEditingDataset] = useState<Dataset | null>(null);

  usePageMeta({
    title: "Datasets para Projetos - Data Hire Club",
    description: "Bases de dados curadas para treinar seus projetos de análise de dados",
    noindex: true,
  });

  const { data: datasets = [], isLoading } = useQuery({
    queryKey: ["community-datasets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_datasets" as any)
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as Dataset[];
    },
  });

  const filteredDatasets = selectedCategory === "Todos"
    ? datasets
    : datasets.filter((d) => d.category.toLowerCase() === selectedCategory.toLowerCase());

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["community-datasets"] });
    queryClient.invalidateQueries({ queryKey: ["datasets-count"] });
    setEditingDataset(null);
  };

  const handleEdit = (dataset: Dataset) => {
    setEditingDataset(dataset);
    setDialogOpen(true);
  };

  const handleToggleActive = async (dataset: Dataset) => {
    try {
      const { error } = await supabase
        .from("community_datasets" as any)
        .update({ is_active: !dataset.is_active, updated_at: new Date().toISOString() } as any)
        .eq("id", dataset.id);

      if (error) throw error;
      toast({ title: dataset.is_active ? "Dataset desativado" : "Dataset ativado" });
      handleSuccess();
    } catch (error: any) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    }
  };

  return (
    <CandidateLayout maxWidth="4xl">
      <div className="bg-gray-50 rounded-lg p-6 md:p-8">
        <CommunityHeader
          title="Datasets para Projetos"
          description="Bases de dados curadas para você treinar projetos reais de análise de dados"
        />

        {/* Banner placeholder - pode ser adicionado futuramente */}

        {isAdmin && (
          <div className="mb-6">
            <Button onClick={() => { setEditingDataset(null); setDialogOpen(true); }} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Adicionar Dataset
            </Button>
          </div>
        )}

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Dataset grid */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : filteredDatasets.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {filteredDatasets.map((dataset) => (
              <DatasetCard
                key={dataset.id}
                dataset={dataset}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            {datasets.length === 0
              ? "Nenhum dataset cadastrado ainda. Em breve teremos bases de dados para você praticar!"
              : "Nenhum dataset encontrado nessa categoria."}
          </div>
        )}
      </div>

      <AddDatasetDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleSuccess}
        editingDataset={editingDataset}
      />
    </CandidateLayout>
  );
};

export default Datasets;
